import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Plus, AlertTriangle, Settings as SettingsIcon, Edit, X, MoveHorizontal, Calendar as CalendarIcon, DollarSign, Scan, Building2, DoorOpen } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import IDScannerModal from '../components/IDScannerModal';
import QuickAddRoomModal from '../components/QuickAddRoomModal';
import OOOModal from '../components/OOOModal';
import RoomViewCard from '../components/RoomViewCard';
import FolioModal from '../components/FolioModal';
import ReservationEditModal from '../components/ReservationEditModal';
import ChangeRoomModal from '../components/ChangeRoomModal';
import AddChargeModal from '../components/AddChargeModal';
import MultiStepReservationModal from '../components/MultiStepReservationModal';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addDays, startOfDay, differenceInCalendarDays, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import wsClient from '../utils/websocket';

const Calendar = () => {
  const { selectedProperty } = useOutletContext();
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewDays, setViewDays] = useState(7); // 7, 14, or 21 days
  const [calendarData, setCalendarData] = useState(null);
  const [calendarVersion, setCalendarVersion] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [guests, setGuests] = useState([]);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState([]);
  const [showTooltips, setShowTooltips] = useState(true);
  const [showRates, setShowRates] = useState(false);
  const [sortMode, setSortMode] = useState('room'); // 'room' or 'type'
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [viewMode, setViewMode] = useState(() => {
    // Load from localStorage, default to 'tape'
    return localStorage.getItem('calendar.viewMode') || 'tape';
  }); // 'tape' or 'room'
  const [user, setUser] = useState(null);
  
  // Reservation creation modal state
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [existingGuestId, setExistingGuestId] = useState('');
  const [useExistingGuest, setUseExistingGuest] = useState(true);
  const [checkIn, setCheckIn] = useState('');
  const [nights, setNights] = useState(1);
  const [adults, setAdults] = useState(1);
  const [stayType, setStayType] = useState('overnight');
  const [baseAmount, setBaseAmount] = useState(100);
  const [taxAmount, setTaxAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  
  // DNR and room status warning
  const [showDNRWarning, setShowDNRWarning] = useState(false);
  const [showRoomWarning, setShowRoomWarning] = useState(false);
  const [dnrDetails, setDNRDetails] = useState(null);
  const [roomWarningStatus, setRoomWarningStatus] = useState('');
  const [pendingCheckIn, setPendingCheckIn] = useState(false);
  
  // Action menu for existing reservations
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(false);
  
  // ID Scanner modal
  const [showScannerModal, setShowScannerModal] = useState(false);
  
  // Quick Add Room modal
  const [showQuickAddRoomModal, setShowQuickAddRoomModal] = useState(false);
  
  // OOO Modal
  const [showOOOModal, setShowOOOModal] = useState(false);
  const [selectedRoomForOOO, setSelectedRoomForOOO] = useState(null);

  // ID image file from reservation modal
  const [pendingIdImageFile, setPendingIdImageFile] = useState(null);

  // Folio Modal
  const [showFolioModal, setShowFolioModal] = useState(false);
  const [folioReservationId, setFolioReservationId] = useState(null);

  // Reservation Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editReservationId, setEditReservationId] = useState(null);

  // Change Room Modal
  const [showChangeRoomModal, setShowChangeRoomModal] = useState(false);
  const [changeRoomReservation, setChangeRoomReservation] = useState(null);

  // Add Charge Modal
  const [showAddChargeModal, setShowAddChargeModal] = useState(false);
  const [addChargeReservation, setAddChargeReservation] = useState(null);

  // Reservation Detail Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailReservation, setDetailReservation] = useState(null);
  
  // Room status dropdown
  const [statusDropdown, setStatusDropdown] = useState({ unitId: null, show: false });

  // Auto-calculate tax when baseAmount changes
  useEffect(() => {
    if (selectedProperty && baseAmount > 0) {
      const totalTaxRate = parseFloat(selectedProperty.tax_total_rate) || 0;
      const calculatedTax = (baseAmount * totalTaxRate) / 100;
      setTaxAmount(parseFloat(calculatedTax.toFixed(2)));
    } else if (baseAmount <= 0 || !baseAmount) {
      setTaxAmount(0);
    }
  }, [baseAmount, selectedProperty]);

  // Calculate date range based on view
  const startDate = currentDate;
  const endDate = addDays(currentDate, viewDays - 1);
  const daysInView = eachDayOfInterval({ start: startDate, end: endDate });
  
  // Sort rooms by number
  const sortedRooms = calendarData?.units ? [...calendarData.units].sort((a, b) => {
    const numA = parseInt(a.name, 10);
    const numB = parseInt(b.name, 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.name.localeCompare(b.name);
  }) : [];
  
  // Filter rooms by selected room types
  const filteredRooms = selectedRoomTypes.length > 0
    ? sortedRooms.filter(room => selectedRoomTypes.includes(room.unit_type_id))
    : sortedRooms;

  // Group rooms by type if sortMode is 'type'
  const groupedRooms = sortMode === 'type' && calendarData?.unit_types
    ? calendarData.unit_types.map(type => ({
        roomTypeId: type.id,
        roomTypeName: type.name,
        smokingPolicy: type.smoking_policy,
        rooms: filteredRooms.filter(r => r.unit_type_id === type.id),
        collapsed: collapsedGroups[type.id] || false
      })).filter(g => g.rooms.length > 0)
    : null;

  const toggleGroup = (typeId) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [typeId]: !prev[typeId]
    }));
  };


  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchCalendarData();
      fetchGuests();
    }
  }, [selectedProperty, currentDate, viewDays]);


  // WebSocket connection and real-time updates
  useEffect(() => {
    if (!selectedProperty) return;

    const token = getToken();
    if (!token) return;

    // Connect WebSocket
    wsClient.connect(selectedProperty.id, token);

    // Handle connection status
    const handleConnection = (data) => {
      setWsConnected(data.connected);
    };

    // Handle reservation events
    const handleReservationEvent = (event) => {
      console.log('[Calendar] Reservation event received:', event.type);
      // Refetch calendar data to get latest
      fetchCalendarData();
    };

    // Handle room status updates
    const handleRoomStatusUpdate = (event) => {
      console.log('[Calendar] Room status updated:', event.payload);
      // Update local state or refetch
      fetchCalendarData();
    };

    // Register event handlers
    wsClient.on('connection', handleConnection);
    wsClient.on('reservation.created', handleReservationEvent);
    wsClient.on('reservation.updated', handleReservationEvent);
    wsClient.on('reservation.deleted', handleReservationEvent);
    wsClient.on('room_status.updated', handleRoomStatusUpdate);
    wsClient.on('guest.checked_in', handleReservationEvent);
    wsClient.on('guest.checked_out', handleReservationEvent);

    // Polling fallback (60 seconds)
    const pollingInterval = setInterval(() => {
      if (!wsConnected) {
        console.log('[Calendar] Polling fallback - refetching data');
        fetchCalendarData();
      }
    }, 60000);

    // Cleanup
    return () => {
      wsClient.off('connection', handleConnection);
      wsClient.off('reservation.created', handleReservationEvent);
      wsClient.off('reservation.updated', handleReservationEvent);
      wsClient.off('reservation.deleted', handleReservationEvent);
      wsClient.off('room_status.updated', handleRoomStatusUpdate);
      wsClient.off('guest.checked_in', handleReservationEvent);
      wsClient.off('guest.checked_out', handleReservationEvent);
      clearInterval(pollingInterval);
      wsClient.disconnect();
    };
  }, [selectedProperty]);


  const getToken = () => localStorage.getItem('access_token');

  const fetchCalendarData = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${API}/calendar?property_id=${selectedProperty.id}&start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(addDays(endDate, 1), 'yyyy-MM-dd')}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check version - only update if changed
      const newVersion = response.data.version || 0;
      if (calendarVersion !== 0 && newVersion === calendarVersion) {
        console.log('[Calendar] Version unchanged, skipping update');
        return;
      }
      
      console.log('[Calendar] Updating data, version:', newVersion);
      setCalendarData(response.data);
      setCalendarVersion(newVersion);
    } catch (error) {
      console.error('Error fetching calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGuests = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/guests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuests(response.data);
    } catch (error) {
      console.error('Error fetching guests:', error);
    }
  };

  const getReservationsForUnit = (unitId, date) => {
    if (!calendarData) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendarData.reservations.filter(res => {
      return res.unit_id === unitId &&
        res.check_in <= dateStr &&
        res.check_out >= dateStr;
    });
  };

  // Get all unique reservations for a room across the visible date range
  const getUnitReservations = (unitId) => {
    if (!calendarData) return [];
    const rangeStart = format(daysInView[0], 'yyyy-MM-dd');
    const rangeEnd = format(daysInView[daysInView.length - 1], 'yyyy-MM-dd');
    return calendarData.reservations.filter(res =>
      res.unit_id === unitId &&
      res.check_in <= rangeEnd &&
      res.check_out >= rangeStart
    );
  };

  // Check if a day cell is free (no active reservation overlaps - allow booking over CHECKED_OUT)
  const isDayCellFree = (unitId, day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    if (!calendarData) return true;
    return !calendarData.reservations.some(res =>
      res.unit_id === unitId &&
      res.check_in <= dateStr &&
      res.check_out >= dateStr &&
      ['CONFIRMED', 'IN_HOUSE', 'TENTATIVE'].includes(res.status) &&
      !(res.status === 'CHECKED_OUT')
    );
  };

  // Compute vertical slot index for overlapping ribbons in the same room
  const getStackedReservations = (unitId) => {
    const reservations = getUnitReservations(unitId);
    // Sort by check_in, then by status (active first)
    const sorted = [...reservations].sort((a, b) => {
      if (a.check_in !== b.check_in) return a.check_in.localeCompare(b.check_in);
      const statusOrder = { IN_HOUSE: 0, CONFIRMED: 1, TENTATIVE: 2, CHECKED_OUT: 3 };
      return (statusOrder[a.status] || 9) - (statusOrder[b.status] || 9);
    });
    // Assign vertical slots - greedy lane assignment
    // For same-day bookings (hourly), force separate lanes so ribbons don't overlap
    const lanes = []; // each lane holds the last check_out date
    return sorted.map(res => {
      let lane = 0;
      const isSameDay = res.check_in === res.check_out;
      for (let i = 0; i < lanes.length; i++) {
        // For same-day stays, don't reuse a lane ending on the same day
        if (isSameDay ? res.check_in > lanes[i] : res.check_in >= lanes[i]) {
          lane = i; break;
        }
        lane = i + 1;
      }
      lanes[lane] = res.check_out;
      return { ...res, _lane: lane };
    });
  };

  // Build OOO pseudo-ribbon for a room
  const getOOORibbon = (unit) => {
    if (unit.status !== 'OOO') return null;
    const vStart = format(daysInView[0], 'yyyy-MM-dd');
    const vEnd = format(daysInView[daysInView.length - 1], 'yyyy-MM-dd');
    const oooStart = unit.maintenance_start || vStart;
    const oooEnd = unit.maintenance_end ? format(new Date(unit.maintenance_end), 'yyyy-MM-dd') : vEnd;
    // Only show if overlaps visible range
    if (oooStart > vEnd || oooEnd < vStart) return null;
    const rStart = oooStart < vStart ? vStart : oooStart;
    const rEnd = oooEnd > vEnd ? vEnd : oooEnd;
    const startIdx = Math.max(0, differenceInCalendarDays(startOfDay(new Date(rStart + 'T00:00:00')), startOfDay(daysInView[0])));
    const endIdx = Math.min(daysInView.length, differenceInCalendarDays(startOfDay(new Date(rEnd + 'T00:00:00')), startOfDay(daysInView[0])) + 1);
    const span = endIdx - startIdx;
    if (span <= 0) return null;
    return { startIdx, span, note: unit.maintenance_note || 'Out of Order' };
  };

  const handleCellClick = (unit, date) => {
    const reservations = getReservationsForUnit(unit.id, date);
    // Only block if there are active (non-checked-out, non-cancelled) reservations
    const activeRes = reservations.filter(r => !['CHECKED_OUT', 'CANCELLED'].includes(r.status));
    if (activeRes.length > 0) {
      return;
    }

    setSelectedUnit(unit);
    setSelectedDate(date);
    setCheckIn(format(date, 'yyyy-MM-dd'));
    setShowReservationModal(true);
  };

  const handleReservationClick = (e, reservation) => {
    e.stopPropagation();
    setSelectedReservation(reservation);
    setShowActionMenu(true);
  };

  const handleRoomStatusClick = (e, unit) => {
    e.stopPropagation();
    if (user && !['owner', 'manager', 'housekeeping'].includes(user.role)) {
      toast.error('You do not have permission to change room status');
      return;
    }
    setStatusDropdown({ unitId: unit.id, show: true });
  };

  const updateRoomStatus = async (unitId, newStatus) => {
    // If changing to OOO, show maintenance modal
    if (newStatus === 'OOO') {
      const unit = sortedRooms.find(u => u.id === unitId);
      if (unit) {
        setSelectedRoomForOOO(unit);
        setShowOOOModal(true);
        setStatusDropdown({ unitId: null, show: false });
      }
      return;
    }
    
    // Close popover immediately
    setStatusDropdown({ unitId: null, show: false });
    
    // Optimistic update — update local state instantly
    setCalendarData(prev => {
      if (!prev?.units) return prev;
      return {
        ...prev,
        units: prev.units.map(u => u.id === unitId ? { ...u, status: newStatus } : u)
      };
    });
    
    try {
      const token = getToken();
      await axios.patch(
        `${API}/units/${unitId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Room status updated to ${newStatus}`);
      fetchCalendarData();
    } catch (error) {
      console.error('Error updating room status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update room status');
      // Revert on failure
      fetchCalendarData();
    }
  };

  const createReservationOrCheckIn = async (andCheckIn = false, forceNewGuest = false, directFormData = null) => {
    // forceNewGuest bypasses useExistingGuest check (used by MultiStepReservationModal)
    const isNewGuest = forceNewGuest || !useExistingGuest;
    
    // If directFormData is provided, use it directly (for MultiStepReservationModal)
    const _guestName = directFormData?.guest_name || guestName;
    const _guestPhone = directFormData?.guest_phone || guestPhone;
    const _guestEmail = directFormData?.guest_email || guestEmail;
    const _documentType = directFormData?.document_type || documentType;
    const _documentNumber = directFormData?.document_number || documentNumber;
    const _checkIn = directFormData?.check_in || checkIn;
    const _checkOut = directFormData?.check_out || format(addDays(new Date(checkIn), nights), 'yyyy-MM-dd');
    const _baseAmount = directFormData ? (parseFloat(directFormData.base_amount) || 0) : baseAmount;
    const _taxAmount = directFormData ? (parseFloat(directFormData.tax_amount) || 0) : taxAmount;
    const _stayType = directFormData?.stay_type || stayType;
    const _paymentMethod = directFormData?.payment_method || paymentMethod;
    const _idImageFile = directFormData?._idImageFile || pendingIdImageFile;
    const _adults = directFormData ? (parseInt(directFormData.adults) || 1) : adults;
    const _children = directFormData ? (parseInt(directFormData.children) || 0) : 0;
    
    if (!isNewGuest && !existingGuestId) {
      toast.error('Please select a guest');
      return;
    }
    if (isNewGuest && !_guestName.trim()) {
      toast.error('Please enter guest name');
      return;
    }

    try {
      const token = getToken();
      let guestId = existingGuestId;
      
      // Create new guest if needed
      if (isNewGuest) {
        const guestResponse = await axios.post(
          `${API}/guests`,
          {
            name: _guestName,
            phone: _guestPhone,
            email: _guestEmail,
            address: directFormData?.guest_address || '',
            city: directFormData?.guest_city || '',
            state: directFormData?.guest_state || '',
            zip_code: directFormData?.guest_zip || '',
            dob: directFormData?.guest_dob || null,
            document_type: _documentType || null,
            document_number: _documentNumber || null,
            vehicle_plate: vehiclePlate || null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        guestId = guestResponse.data.id;

        // Upload ID image if attached
        if (_idImageFile) {
          console.log('[Reservation] ID photo file:', _idImageFile.name, _idImageFile.size, 'bytes');
          if (_idImageFile.size < 500) {
            toast.error('ID photo appears empty. Please retake from View/Edit > Guest Info.');
          } else {
            try {
              const imgFormData = new FormData();
              imgFormData.append('file', _idImageFile);
              await axios.post(`${API}/guests/${guestId}/upload-id-image`, imgFormData, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success('ID photo saved');
            } catch (imgErr) {
              console.error('Failed to upload ID image:', imgErr);
              toast.error('Failed to save ID photo. You can upload it later from View/Edit > Guest Info.');
            }
          }
          setPendingIdImageFile(null);
        }
      }

      const reservationData = {
        property_id: selectedProperty.id,
        unit_id: selectedUnit.id,
        guest_id: guestId,
        check_in: _checkIn,
        check_out: _checkOut,
        adults: _adults,
        children: _children,
        source: 'Direct',
        status: 'CONFIRMED',
        stay_type: _stayType,
        base_amount: _baseAmount,
        tax_amount: _taxAmount,
        notes: ''
      };

      const resResponse = await axios.post(
        `${API}/reservations`,
        reservationData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // If checking in immediately
      if (andCheckIn) {
        const _paymentAmt = directFormData ? (parseFloat(directFormData.payment_amount) || 0) : 0;
        const checkinRes = await axios.post(
          `${API}/reservations/${resResponse.data.id}/checkin`,
          {
            payment_amount: _paymentAmt,
            payment_method: _paymentMethod,
            override_room_status: true
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success(andCheckIn ? 'Guest checked in successfully' : 'Reservation created successfully');
      setShowReservationModal(false);
      resetReservationForm();
      fetchCalendarData();
      
      // Show folio after check-in
      if (andCheckIn) {
        setFolioReservationId(resResponse.data.id);
        setShowFolioModal(true);
      }
      
    } catch (error) {
      console.error('Error creating reservation:', error);
      
      if (error.response?.status === 403 && error.response?.data?.detail?.do_not_rent) {
        setDNRDetails(error.response.data.detail);
        setShowDNRWarning(true);
        setShowReservationModal(false);
      } else if (error.response?.data?.requires_override) {
        setRoomWarningStatus(error.response.data.room_status);
        setShowRoomWarning(true);
        setPendingCheckIn(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create reservation');
      }
    }
  };

  const handleCheckInAction = async (reservation) => {
    try {
      const token = getToken();
      const response = await axios.post(
        `${API}/reservations/${reservation.id}/checkin`,
        {
          payment_amount: 0,
          payment_method: 'Cash',
          override_room_status: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.requires_override) {
        setRoomWarningStatus(response.data.room_status);
        setShowRoomWarning(true);
        setSelectedReservation(reservation);
      } else {
        toast.success('Guest checked in successfully');
        setShowActionMenu(false);
        fetchCalendarData();
        // Show folio after check-in
        setFolioReservationId(reservation.id);
        setShowFolioModal(true);
      }
    } catch (error) {
      console.error('Error checking in:', error);
      toast.error('Failed to check in guest');
    }
  };

  const handleCheckOutAction = async (reservation) => {
    try {
      const token = getToken();
      await axios.post(
        `${API}/reservations/${reservation.id}/checkout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Guest checked out successfully');
      setShowActionMenu(false);
      fetchCalendarData();
    } catch (error) {
      console.error('Error checking out:', error);
      toast.error('Failed to check out guest');
    }
  };

  const handleExtendStay = async (reservation, days) => {
    const newCheckOut = format(addDays(new Date(reservation.check_out + 'T00:00:00'), days), 'yyyy-MM-dd');
    try {
      const token = getToken();
      await axios.patch(
        `${API}/reservations/${reservation.id}/extend`,
        null,
        {
          params: { new_check_out: newCheckOut },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(`Stay extended to ${newCheckOut}`);
      setShowActionMenu(false);
      // Force version reset to ensure calendar refreshes
      setCalendarVersion(0);
      fetchCalendarData();
    } catch (error) {
      console.error('Error extending stay:', error);
      toast.error(error.response?.data?.detail || 'Failed to extend stay');
    }
  };

  const handleScannedData = async (scannedData) => {
    if (!scannedData) return;

    // Fill form with scanned data
    if (!useExistingGuest) {
      if (scannedData.fullName) {
        setGuestName(scannedData.fullName);
      } else if (scannedData.firstName || scannedData.lastName) {
        setGuestName([scannedData.firstName, scannedData.lastName].filter(Boolean).join(' '));
      }
      
      if (scannedData.documentNumber) {
        setDocumentNumber(scannedData.documentNumber);
        
        // Immediately run DNR check
        try {
          const token = getToken();
          const response = await axios.post(
            `${API}/dnr/check`,
            null,
            {
              params: { document_number: scannedData.documentNumber },
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          
          if (response.data.do_not_rent) {
            setDNRDetails(response.data);
            setShowDNRWarning(true);
          } else {
            toast.success('ID scanned successfully');
          }
        } catch (error) {
          console.error('Error checking DNR:', error);
        }
      }
      
      // Set other fields if available
      if (scannedData.documentType) setDocumentType(scannedData.documentType);
      if (scannedData.addressLine1) {
        // Could set address field if we add it to the form
      }
    }
    
    setShowScannerModal(false);
  };

  const resetReservationForm = () => {
    setGuestName('');
    setGuestPhone('');
    setGuestEmail('');
    setExistingGuestId('');
    setUseExistingGuest(true);
    setCheckIn('');
    setNights(1);
    setAdults(1);
    setStayType('overnight');
    setBaseAmount(100);
    setTaxAmount(0);
    setPaymentMethod('Cash');
    setDocumentType('');
    setDocumentNumber('');
    setVehiclePlate('');
    setShowMoreDetails(false);
    setSelectedUnit(null);
    setSelectedDate(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      TENTATIVE: 'bg-amber-200 border-amber-400 text-amber-900',
      CONFIRMED: 'bg-blue-200 border-blue-400 text-blue-900',
      IN_HOUSE: 'bg-emerald-200 border-emerald-400 text-emerald-900',
      CHECKED_OUT: 'bg-red-200 border-red-400 text-red-900',
      CANCELLED: 'bg-red-100 border-red-300 text-red-800',
      OOO: 'bg-slate-300 border-slate-500 text-slate-800'
    };
    return colors[status] || 'bg-slate-200 border-slate-400 text-slate-900';
  };

  const getRoomStatusColor = (status) => {
    const colors = {
      CLEAN: 'bg-emerald-500',
      DIRTY: 'bg-red-500',
      IN_PROGRESS: 'bg-amber-500',
      INSPECTED: 'bg-blue-500',
      OCCUPIED: 'bg-purple-500',
      OOO: 'bg-slate-400'
    };
    return colors[status] || 'bg-slate-400';
  };

  const canManageReservations = user && ['owner', 'manager', 'front_desk'].includes(user.role);
  const canChangeRoomStatus = user && ['owner', 'manager', 'housekeeping'].includes(user.role);

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">{t('common.selectProperty')}</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('calendar.title')}</h1>
          {/* Show date range only in Tape View */}
          {viewMode === 'tape' && (
            <p className="text-slate-600 mt-1">
              {format(startDate, 'MMM dd')} - {format(endDate, 'MMM dd, yyyy')}
            </p>
          )}
          {/* Show single date in Room View */}
          {viewMode === 'room' && (
            <p className="text-slate-600 mt-1">
              {format(currentDate, 'EEEE, MMMM dd, yyyy')}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Connection Status Indicator */}
          {selectedProperty && (
            <div 
              className="flex items-center gap-2 text-xs px-2 py-1 rounded-full bg-slate-50"
              title={wsConnected ? 'Real-time updates active' : 'Updates every 60 seconds'}
            >
              <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
              <span className={wsConnected ? 'text-green-700 font-medium' : 'text-slate-600'}>
                {wsConnected ? 'Live Updates' : 'Auto-Refresh'}
              </span>
            </div>
          )}

          {/* View Mode Tabs */}
          <div className="flex gap-1 border border-slate-300 rounded-md p-1 bg-slate-50">
            <Button
              size="sm"
              variant={viewMode === 'tape' ? 'default' : 'ghost'}
              onClick={() => {
                setViewMode('tape');
                localStorage.setItem('calendar.viewMode', 'tape');
              }}
              className="h-8"
            >
              Tape Chart
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'room' ? 'default' : 'ghost'}
              onClick={() => {
                setViewMode('room');
                localStorage.setItem('calendar.viewMode', 'room');
              }}
              className="h-8"
            >
              Room View
            </Button>
          </div>


          {/* Sort Mode Toggle - Only in Tape View */}
          {viewMode === 'tape' && (
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm"
            >
              <option value="room">Sort by Room</option>
              <option value="type">Group by Type</option>
            </select>
          )}

          {/* Display Options */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" title="Display Options">
                <SettingsIcon size={16} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="end">
              <div className="space-y-2">
                <p className="font-medium text-sm mb-2">Display Options</p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showTooltips}
                    onChange={(e) => {
                      setShowTooltips(e.target.checked);
                      localStorage.setItem('calendar.showTooltips', e.target.checked);
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">Show Guest Details</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showRates}
                    onChange={(e) => {
                      setShowRates(e.target.checked);
                      localStorage.setItem('calendar.showRates', e.target.checked);
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">Show rates</span>
                </label>
              </div>
            </PopoverContent>
          </Popover>

          
          {/* View Selector - Only in Tape View */}
          {viewMode === 'tape' && (
            <select
              value={viewDays}
              onChange={(e) => setViewDays(parseInt(e.target.value))}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm"
            >
              <option value={7}>1 Week</option>
              <option value={14}>2 Weeks</option>
              <option value={21}>3 Weeks</option>
            </select>
          )}

          {/* Navigation Controls - Different for each view */}
          <div className="flex items-center gap-1 border border-slate-300 rounded-md p-1">
            {/* Week navigation - Only in Tape View */}
            {viewMode === 'tape' && (
              <Button 
                variant="ghost" 
                onClick={() => setCurrentDate(prev => addDays(prev, -viewDays))} 
                size="sm"
                title="Previous Week"
                className="h-8 w-8 p-0"
              >
                <ChevronsLeft size={16} />
              </Button>
            )}
            
            {/* Day navigation - Always visible */}
            <Button 
              variant="ghost" 
              onClick={() => setCurrentDate(prev => addDays(prev, -1))} 
              size="sm"
              title="Previous Day"
              className="h-8 w-8 p-0"
            >
              <ChevronLeft size={16} />
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => setCurrentDate(new Date())} 
              size="sm"
              title="Go to Today"
              className="h-8 px-3"
            >
              Today
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => setCurrentDate(prev => addDays(prev, 1))} 
              size="sm"
              title="Next Day"
              className="h-8 w-8 p-0"
            >
              <ChevronRight size={16} />
            </Button>
            
            {/* Week navigation - Only in Tape View */}
            {viewMode === 'tape' && (
              <Button 
                variant="ghost" 
                onClick={() => setCurrentDate(prev => addDays(prev, viewDays))} 
                size="sm"
                title="Next Week"
                className="h-8 w-8 p-0"
              >
                <ChevronsRight size={16} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Room Type Filter - Moved up for better space usage */}
      {selectedProperty && calendarData?.unit_types && calendarData.unit_types.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-700">Filter:</span>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={selectedRoomTypes.length === 0 ? "default" : "outline"}
                  onClick={() => setSelectedRoomTypes([])}
                >
                  All ({sortedRooms.length})
                </Button>
                {calendarData.unit_types.map(type => {
                  const count = sortedRooms.filter(r => r.unit_type_id === type.id).length;
                  const isSelected = selectedRoomTypes.includes(type.id);
                  return (
                    <Button
                      key={type.id}
                      size="sm"
                      variant={isSelected ? "default" : "outline"}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedRoomTypes(prev => prev.filter(id => id !== type.id));
                        } else {
                          setSelectedRoomTypes(prev => [...prev, type.id]);
                        }
                      }}
                    >
                      {type.name} ({count})
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}



      {/* Empty State - No Properties */}
      {!selectedProperty && (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No properties yet</h3>
            <p className="text-slate-600 mb-4">Add your first property to start using the calendar.</p>
            <Button onClick={() => window.location.href = '/settings'}>
              <Plus size={16} className="mr-2" />
              Add Property
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Empty State - No Rooms */}
      {selectedProperty && sortedRooms.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <DoorOpen size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">This property has no rooms configured yet</h3>
            <p className="text-slate-600 mb-4">Add rooms to start managing reservations.</p>
            <Button onClick={() => setShowQuickAddRoomModal(true)}>
              <Plus size={16} className="mr-2" />
              Add Rooms
            </Button>
          </CardContent>
        </Card>
      )}

      {selectedProperty && sortedRooms.length > 0 && viewMode === 'tape' && (
      <Card data-testid="calendar-grid">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <div className="min-w-[1000px]">
              {/* Calculate grid template - Room column 120px */}
              <div style={{ display: 'grid', gridTemplateColumns: `120px 1fr` }}>
                {/* Header */}
                <div className="p-3 font-semibold text-slate-700 border-b border-r border-slate-200 bg-slate-50 text-sm">
                  Room
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${daysInView.length}, minmax(110px, 1fr))` }}>
                {daysInView.map((day) => (
                  <div key={day.toString()} className="p-3 text-center border-b border-r border-slate-200 bg-slate-50">
                    <div className="font-semibold text-slate-900 text-sm">{format(day, 'EEE')}</div>
                    <div className="text-xs text-slate-600">{format(day, 'MMM dd')}</div>
                  </div>
                ))}
                </div>
              </div>

              {/* Room rows */}
              {calendarData?.units.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  No rooms configured. Add rooms in Settings.
                </div>
              ) : sortMode === 'type' && groupedRooms ? (
                // Grouped by Room Type
                groupedRooms.map((group) => (
                  <React.Fragment key={group.roomTypeId}>
                    {/* Group Header */}
                    <div 
                      className="bg-slate-100 hover:bg-slate-200 cursor-pointer"
                      style={{ display: 'grid', gridTemplateColumns: `120px 1fr` }}
                      onClick={() => toggleGroup(group.roomTypeId)}
                    >
                      <div className="p-3 border-b border-r border-slate-200 font-semibold text-slate-800 flex items-center gap-2">
                        <span>{group.collapsed ? '▶' : '▼'}</span>
                        <span>{group.roomTypeName} ({group.rooms.length})</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${daysInView.length}, minmax(110px, 1fr))` }}>
                        {daysInView.map((day) => (
                          <div key={day.toString()} className="border-b border-r border-slate-200"></div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Room Rows in Group */}
                    {!group.collapsed && group.rooms.map((unit) => (
                      <div 
                        key={unit.id} 
                        className="hover:bg-slate-50"
                        style={{ display: 'grid', gridTemplateColumns: `120px 1fr` }}
                      >
                    <div className="p-3 border-b border-r border-slate-200">
                      <div className="font-medium text-slate-900 flex items-center gap-1">
                        {unit.name}
                        {/* Show smoking icon from room itself */}
                        {unit.smoking_policy === 'SMOKING' && (
                          <span title="Smoking Allowed" className="text-lg">🚬</span>
                        )}
                        {unit.smoking_policy === 'NON_SMOKING' && (
                          <span title="Non-Smoking" className="text-lg">🚭</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {canChangeRoomStatus ? (
                          <Popover open={statusDropdown.unitId === unit.id && statusDropdown.show} onOpenChange={(open) => {
                            if (!open && statusDropdown.unitId === unit.id) {
                              setStatusDropdown({ unitId: null, show: false });
                            }
                          }}>
                            <PopoverTrigger asChild>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStatusDropdown({ unitId: unit.id, show: true });
                                }}
                                className="flex items-center gap-1 hover:opacity-80 cursor-pointer focus:outline-none"
                              >
                                <span className={`inline-block w-2 h-2 rounded-full ${getRoomStatusColor(unit.status)}`}></span>
                                <span className="underline">{unit.status}</span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent 
                              className="w-40 p-2" 
                              align="start"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="space-y-1">
                                {['CLEAN', 'DIRTY', 'IN_PROGRESS', 'INSPECTED', 'OOO'].map((status) => (
                                  <button
                                    key={status}
                                    onClick={() => updateRoomStatus(unit.id, status)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 rounded flex items-center gap-2 transition"
                                  >
                                    <span className={`w-2 h-2 rounded-full ${getRoomStatusColor(status)}`}></span>
                                    {status}
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className={`inline-block w-2 h-2 rounded-full ${getRoomStatusColor(unit.status)}`}></span>
                            <span>{unit.status}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Days container with ribbon overlays */}
                    {(() => {
                      const stacked = getStackedReservations(unit.id);
                      const laneCount = stacked.length > 0 ? Math.max(...stacked.map(r => r._lane)) + 1 : 1;
                      const cellH = Math.max(60, laneCount * 24 + 12);
                      const ribbonH = laneCount > 1 ? Math.floor((cellH - 8) / laneCount) : 28;
                      return (
                    <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: `repeat(${daysInView.length}, minmax(110px, 1fr))` }}>
                      {/* Empty day cells for borders and click targets */}
                      {daysInView.map((day) => {
                        const free = isDayCellFree(unit.id, day);
                        return (
                          <div
                            key={`${unit.id}-${day.toString()}`}
                            className={`border-b border-r border-slate-200 ${free && canManageReservations ? 'cursor-pointer hover:bg-cyan-50 transition' : ''}`}
                            style={{ minHeight: cellH, position: 'relative', zIndex: free ? 5 : 0 }}
                            onClick={() => free && canManageReservations && handleCellClick(unit, day)}
                            title={free && canManageReservations ? 'Click to create reservation' : ''}
                          >
                            {free && canManageReservations && (
                              <div className="flex items-center justify-center h-full opacity-0 hover:opacity-100 transition">
                                <Plus size={16} className="text-cyan-600" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Reservation ribbons - stacked */}
                      {stacked.map((res) => {
                        const ciDate = startOfDay(new Date(res.check_in + 'T00:00:00'));
                        const coDate = startOfDay(new Date(res.check_out + 'T00:00:00'));
                        const vStart = startOfDay(daysInView[0]);
                        const vEnd = startOfDay(daysInView[daysInView.length - 1]);
                        const rStart = ciDate < vStart ? vStart : ciDate;
                        const rEnd = coDate > vEnd ? vEnd : coDate;
                        const startIdx = Math.max(0, differenceInCalendarDays(rStart, vStart));
                        const endIdx = Math.min(daysInView.length, differenceInCalendarDays(rEnd, vStart) + 1);
                        const span = endIdx - startIdx;
                        if (span <= 0) return null;
                        const pct = 100 / daysInView.length;
                        const topPos = 4 + res._lane * (ribbonH + 2);
                        const isCheckedOut = res.status === 'CHECKED_OUT';
                        return (
                          <div
                            key={res.id}
                            className={`absolute cursor-pointer hover:opacity-90 transition-opacity rounded shadow-sm ${getStatusColor(res.status)} border ${isCheckedOut ? 'opacity-50' : ''}`}
                            style={{
                              top: topPos, height: ribbonH,
                              left: `${startIdx * pct}%`,
                              width: `${span * pct}%`,
                              zIndex: isCheckedOut ? 1 : 2,
                              display: 'flex', alignItems: 'center',
                              padding: '0 6px', overflow: 'hidden',
                              pointerEvents: 'auto'
                            }}
                            onClick={(e) => handleReservationClick(e, res)}
                            title={`${res.guest?.name || 'Guest'}\n${res.check_in} → ${res.check_out}\n${res.status}${showRates ? `\nTotal: $${(res.base_amount + res.tax_amount).toFixed(2)}` : ''}`}
                          >
                            <div className="text-xs font-medium truncate">{res.guest?.name || 'Guest'}</div>
                            {span > 2 && <div className="text-xs opacity-60 ml-2 hidden sm:block">{res.check_in} → {res.check_out}</div>}
                            {showRates && span > 1 && <div className="text-xs opacity-60 ml-auto hidden sm:block">${res.base_amount}</div>}
                          </div>
                        );
                      })}
                      {/* OOO ribbon */}
                      {(() => {
                        const ooo = getOOORibbon(unit);
                        if (!ooo) return null;
                        const pct = 100 / daysInView.length;
                        return (
                          <div
                            key="ooo"
                            className="absolute rounded shadow-sm bg-slate-300 border border-slate-500 text-slate-800"
                            style={{
                              top: 4, height: 28,
                              left: `${ooo.startIdx * pct}%`,
                              width: `${ooo.span * pct}%`,
                              zIndex: 3,
                              display: 'flex', alignItems: 'center',
                              padding: '0 6px', overflow: 'hidden'
                            }}
                            title={`Out of Order: ${ooo.note}`}
                          >
                            <div className="text-xs font-medium truncate">OOO — {ooo.note}</div>
                          </div>
                        );
                      })()}
                      {/* Re-book button for free cells with checked-out ribbons */}
                      {daysInView.map((day, dayIdx) => {
                        const free = isDayCellFree(unit.id, day);
                        const hasCheckedOut = calendarData?.reservations.some(r =>
                          r.unit_id === unit.id &&
                          r.check_in <= format(day, 'yyyy-MM-dd') &&
                          r.check_out >= format(day, 'yyyy-MM-dd') &&
                          r.status === 'CHECKED_OUT'
                        );
                        if (!free || !hasCheckedOut || !canManageReservations) return null;
                        const pct = 100 / daysInView.length;
                        return (
                          <div
                            key={`rebook-${unit.id}-${dayIdx}`}
                            className="absolute flex items-end justify-center pb-1 cursor-pointer"
                            style={{
                              left: `${dayIdx * pct}%`,
                              width: `${pct}%`,
                              bottom: 0, height: 24,
                              zIndex: 10
                            }}
                            onClick={() => handleCellClick(unit, day)}
                            title="Click to rent this room again"
                          >
                            <div className="bg-cyan-600 text-white text-xs px-2 py-0.5 rounded-full shadow hover:bg-cyan-700 transition" data-testid={`rebook-btn-${unit.id}`}>
                              + Re-rent
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    );
                    })()}
                  </div>
                    ))}
                  </React.Fragment>
                ))
              ) : (
                // Regular view - sorted by room number
                filteredRooms.map((unit) => (
                  <div 
                    key={unit.id} 
                    className="hover:bg-slate-50"
                    style={{ display: 'grid', gridTemplateColumns: `120px 1fr` }}
                  >
                    {/* Room cell - reuse existing structure */}
                    <div className="p-3 border-b border-r border-slate-200">
                      <div className="font-medium text-slate-900 flex items-center gap-1">
                        {unit.name}
                        {unit.smoking_policy === 'SMOKING' && (
                          <span title="Smoking Allowed" className="text-lg">🚬</span>
                        )}
                        {unit.smoking_policy === 'NON_SMOKING' && (
                          <span title="Non-Smoking" className="text-lg">🚭</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {canChangeRoomStatus ? (
                          <Popover open={statusDropdown.unitId === unit.id && statusDropdown.show} onOpenChange={(open) => {
                            if (!open && statusDropdown.unitId === unit.id) {
                              setStatusDropdown({ unitId: null, show: false });
                            }
                          }}>
                            <PopoverTrigger asChild>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStatusDropdown({ unitId: unit.id, show: true });
                                }}
                                className="flex items-center gap-1 hover:opacity-80 cursor-pointer focus:outline-none"
                              >
                                <span className={`inline-block w-2 h-2 rounded-full ${getRoomStatusColor(unit.status)}`}></span>
                                <span className="underline">{unit.status}</span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent 
                              className="w-40 p-2" 
                              align="start"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="space-y-1">
                                {['CLEAN', 'DIRTY', 'IN_PROGRESS', 'INSPECTED', 'OOO'].map((status) => (
                                  <button
                                    key={status}
                                    onClick={() => updateRoomStatus(unit.id, status)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 rounded flex items-center gap-2 transition"
                                  >
                                    <span className={`w-2 h-2 rounded-full ${getRoomStatusColor(status)}`}></span>
                                    {status}
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className={`inline-block w-2 h-2 rounded-full ${getRoomStatusColor(unit.status)}`}></span>
                            <span>{unit.status}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Days container with ribbon overlays */}
                    {(() => {
                      const stacked = getStackedReservations(unit.id);
                      const laneCount = stacked.length > 0 ? Math.max(...stacked.map(r => r._lane)) + 1 : 1;
                      const cellH = Math.max(60, laneCount * 24 + 12);
                      const ribbonH = laneCount > 1 ? Math.floor((cellH - 8) / laneCount) : 28;
                      return (
                    <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: `repeat(${daysInView.length}, minmax(110px, 1fr))` }}>
                      {daysInView.map((day) => {
                        const free = isDayCellFree(unit.id, day);
                        return (
                          <div
                            key={`${unit.id}-${day.toString()}`}
                            className={`border-b border-r border-slate-200 ${free && canManageReservations ? 'cursor-pointer hover:bg-cyan-50 transition' : ''}`}
                            style={{ minHeight: cellH, position: 'relative', zIndex: free ? 5 : 0 }}
                            onClick={() => free && canManageReservations && handleCellClick(unit, day)}
                            title={free && canManageReservations ? 'Click to create reservation' : ''}
                          >
                            {free && canManageReservations && (
                              <div className="flex items-center justify-center h-full opacity-0 hover:opacity-100 transition">
                                <Plus size={16} className="text-cyan-600" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {stacked.map((res) => {
                        const ciDate = startOfDay(new Date(res.check_in + 'T00:00:00'));
                        const coDate = startOfDay(new Date(res.check_out + 'T00:00:00'));
                        const vStart = startOfDay(daysInView[0]);
                        const vEnd = startOfDay(daysInView[daysInView.length - 1]);
                        const rStart = ciDate < vStart ? vStart : ciDate;
                        const rEnd = coDate > vEnd ? vEnd : coDate;
                        const startIdx = Math.max(0, differenceInCalendarDays(rStart, vStart));
                        const endIdx = Math.min(daysInView.length, differenceInCalendarDays(rEnd, vStart) + 1);
                        const span = endIdx - startIdx;
                        if (span <= 0) return null;
                        const pct = 100 / daysInView.length;
                        const topPos = 4 + res._lane * (ribbonH + 2);
                        const isCheckedOut = res.status === 'CHECKED_OUT';
                        return (
                          <div
                            key={res.id}
                            className={`absolute cursor-pointer hover:opacity-90 transition-opacity rounded shadow-sm ${getStatusColor(res.status)} border ${isCheckedOut ? 'opacity-50' : ''}`}
                            style={{
                              top: topPos, height: ribbonH,
                              left: `${startIdx * pct}%`,
                              width: `${span * pct}%`,
                              zIndex: isCheckedOut ? 1 : 2,
                              display: 'flex', alignItems: 'center',
                              padding: '0 6px', overflow: 'hidden',
                              pointerEvents: 'auto'
                            }}
                            onClick={(e) => handleReservationClick(e, res)}
                            title={`${res.guest?.name || 'Guest'}\n${res.check_in} → ${res.check_out}\n${res.status}${showRates ? `\nTotal: $${(res.base_amount + res.tax_amount).toFixed(2)}` : ''}`}
                          >
                            <div className="text-xs font-medium truncate">{res.guest?.name || 'Guest'}</div>
                            {span > 2 && <div className="text-xs opacity-60 ml-2 hidden sm:block">{res.check_in} → {res.check_out}</div>}
                            {showRates && span > 1 && <div className="text-xs opacity-60 ml-auto hidden sm:block">${res.base_amount}</div>}
                          </div>
                        );
                      })}
                      {/* OOO ribbon */}
                      {(() => {
                        const ooo = getOOORibbon(unit);
                        if (!ooo) return null;
                        const pct = 100 / daysInView.length;
                        return (
                          <div
                            key="ooo"
                            className="absolute rounded shadow-sm bg-slate-300 border border-slate-500 text-slate-800"
                            style={{
                              top: 4, height: 28,
                              left: `${ooo.startIdx * pct}%`,
                              width: `${ooo.span * pct}%`,
                              zIndex: 3,
                              display: 'flex', alignItems: 'center',
                              padding: '0 6px', overflow: 'hidden'
                            }}
                            title={`Out of Order: ${ooo.note}`}
                          >
                            <div className="text-xs font-medium truncate">OOO — {ooo.note}</div>
                          </div>
                        );
                      })()}
                      {/* Re-book button for free cells with checked-out ribbons */}
                      {daysInView.map((day, dayIdx) => {
                        const free = isDayCellFree(unit.id, day);
                        const hasCheckedOut = calendarData?.reservations.some(r =>
                          r.unit_id === unit.id &&
                          r.check_in <= format(day, 'yyyy-MM-dd') &&
                          r.check_out >= format(day, 'yyyy-MM-dd') &&
                          r.status === 'CHECKED_OUT'
                        );
                        if (!free || !hasCheckedOut || !canManageReservations) return null;
                        const pct = 100 / daysInView.length;
                        return (
                          <div
                            key={`rebook-${unit.id}-${dayIdx}`}
                            className="absolute flex items-end justify-center pb-1 cursor-pointer"
                            style={{
                              left: `${dayIdx * pct}%`,
                              width: `${pct}%`,
                              bottom: 0, height: 24,
                              zIndex: 10
                            }}
                            onClick={() => handleCellClick(unit, day)}
                            title="Click to rent this room again"
                          >
                            <div className="bg-cyan-600 text-white text-xs px-2 py-0.5 rounded-full shadow hover:bg-cyan-700 transition" data-testid={`rebook-btn-${unit.id}`}>
                              + Re-rent
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    );
                    })()}
                  </div>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      )}


      {/* Room View - Card Layout */}
      {selectedProperty && viewMode === 'room' && sortedRooms.length > 0 && (
        <div className="space-y-6">
          {/* Group rooms by floor */}
          {(() => {
            const groupedByFloor = filteredRooms.reduce((acc, room) => {
              const floor = room.floor || 1;
              if (!acc[floor]) acc[floor] = [];
              acc[floor].push(room);
              return acc;
            }, {});

            return Object.keys(groupedByFloor).sort((a, b) => parseInt(a) - parseInt(b)).map(floor => {
              const roomsOnFloor = groupedByFloor[floor];
              
              return (
                <div key={floor}>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">
                    Floor {floor} ({roomsOnFloor.length} rooms)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {roomsOnFloor.map(room => {
                      const roomType = calendarData?.unit_types?.find(t => t.id === room.unit_type_id);
                      const reservation = calendarData?.reservations?.find(r => 
                        r.unit_id === room.id && 
                        r.check_in <= format(currentDate, 'yyyy-MM-dd') &&
                        r.check_out > format(currentDate, 'yyyy-MM-dd') &&
                        ['CONFIRMED', 'IN_HOUSE'].includes(r.status)
                      );

                      return (
                        <RoomViewCard
                          key={room.id}
                          room={room}
                          roomType={roomType}
                          reservation={reservation}
                          canChangeRoomStatus={canChangeRoomStatus}
                          onStatusChange={updateRoomStatus}
                          onAddReservation={(room) => {
                            setSelectedUnit(room);
                            setSelectedDate(currentDate);
                            setCheckIn(format(currentDate, 'yyyy-MM-dd'));
                            setShowReservationModal(true);
                          }}
                          onViewReservation={(res) => {
                            setSelectedReservation(res);
                            setShowActionMenu(true);
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}


      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-200 border border-blue-400"></div>
          <span className="text-slate-600">Reserved</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-emerald-200 border border-emerald-400"></div>
          <span className="text-slate-600">In-House</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-200 border border-red-400"></div>
          <span className="text-slate-600">Checked Out</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-slate-300 border border-slate-500"></div>
          <span className="text-slate-600">Out of Order</span>
        </div>
        {canManageReservations && (
          <div className="ml-auto text-xs text-slate-500">
            Click empty cells to create &middot; Click ribbons for actions &middot; Click status to change
          </div>
        )}
      </div>

      {/* New Reservation Modal - Multi-Step */}
      <MultiStepReservationModal
        open={showReservationModal}
        onClose={() => {
          setShowReservationModal(false);
          resetReservationForm();
        }}
        property={selectedProperty}
        initialData={{
          check_in: checkIn,
          check_out: checkIn ? format(addDays(new Date(checkIn), nights), 'yyyy-MM-dd') : '',
          unit_id: selectedUnit?.id,
          adults: adults,
          base_amount: baseAmount,
          tax_amount: taxAmount
        }}
        onSubmit={(formData) => {
          // MultiStepReservationModal always creates new guests
          // Pass formData directly to avoid async state timing issues
          const _now = new Date();
          const _today = `${_now.getFullYear()}-${String(_now.getMonth()+1).padStart(2,'0')}-${String(_now.getDate()).padStart(2,'0')}`;
          const isToday = formData.check_in === _today;
          createReservationOrCheckIn(isToday, true, formData);
        }}
      />


      {/* Reservation Action Menu */}
      <Dialog open={showActionMenu} onOpenChange={setShowActionMenu}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reservation Actions</DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedReservation.guest?.name}</p>
                <p className="text-sm text-slate-600">Room: {selectedReservation.unit_id}</p>
                <p className="text-sm text-slate-600">
                  {selectedReservation.check_in} to {selectedReservation.check_out}
                </p>
                <p className="text-xs text-slate-500 mt-1">Status: {selectedReservation.status}</p>
              </div>

              {/* Actions based on status */}
              {selectedReservation.status === 'CONFIRMED' && canManageReservations && (
                <div className="space-y-2">
                  <Button className="w-full" onClick={() => handleCheckInAction(selectedReservation)}>
                    Check In
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => {
                    setEditReservationId(selectedReservation.id);
                    setShowEditModal(true);
                    setShowActionMenu(false);
                  }}>
                    <Edit className="mr-2" size={16} />
                    View / Edit
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => {
                    setChangeRoomReservation(selectedReservation);
                    setShowChangeRoomModal(true);
                    setShowActionMenu(false);
                  }}>
                    <MoveHorizontal className="mr-2" size={16} />
                    Change Room
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => handleExtendStay(selectedReservation, 1)}>
                    <CalendarIcon className="mr-2" size={16} />
                    Extend Stay (+1 night)
                  </Button>
                  <Button variant="destructive" className="w-full" onClick={async () => {
                    try {
                      const token = localStorage.getItem('access_token');
                      await axios.patch(`${API}/reservations/${selectedReservation.id}`, { status: 'CANCELLED' }, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success('Reservation cancelled');
                      setShowActionMenu(false);
                      fetchCalendarData();
                    } catch { toast.error('Failed to cancel'); }
                  }}>
                    <X className="mr-2" size={16} />
                    Cancel Reservation
                  </Button>
                </div>
              )}

              {selectedReservation.status === 'IN_HOUSE' && canManageReservations && (
                <div className="space-y-2">
                  <Button className="w-full" onClick={() => handleCheckOutAction(selectedReservation)}>
                    Check Out
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => {
                    setEditReservationId(selectedReservation.id);
                    setShowEditModal(true);
                    setShowActionMenu(false);
                  }}>
                    <Edit className="mr-2" size={16} />
                    View / Edit
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => {
                    setAddChargeReservation(selectedReservation);
                    setShowAddChargeModal(true);
                    setShowActionMenu(false);
                  }}>
                    <DollarSign className="mr-2" size={16} />
                    Add Charge
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => {
                    setChangeRoomReservation(selectedReservation);
                    setShowChangeRoomModal(true);
                    setShowActionMenu(false);
                  }}>
                    <MoveHorizontal className="mr-2" size={16} />
                    Change Room
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => handleExtendStay(selectedReservation, 1)}>
                    <CalendarIcon className="mr-2" size={16} />
                    Extend Stay (+1 night)
                  </Button>
                  <Button variant="destructive" className="w-full" onClick={async () => {
                    if (!window.confirm('Cancel this check-in? All payments will be removed and the reservation will revert to Confirmed.')) return;
                    try {
                      const token = localStorage.getItem('access_token');
                      await axios.post(`${API}/reservations/${selectedReservation.id}/cancel-checkin`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success('Check-in cancelled. Reservation reverted to Confirmed.');
                      setShowActionMenu(false);
                      fetchCalendarData();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel check-in'); }
                  }}>
                    <X className="mr-2" size={16} />
                    Cancel Check-In
                  </Button>
                </div>
              )}

              {(selectedReservation.status === 'CHECKED_OUT' || selectedReservation.status === 'CANCELLED') && (
                <div className="space-y-2">
                  <Button variant="outline" className="w-full" onClick={() => {
                    setEditReservationId(selectedReservation.id);
                    setShowEditModal(true);
                    setShowActionMenu(false);
                  }}>
                    <Edit className="mr-2" size={16} />
                    View Details
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* DNR Warning Modal */}
      <Dialog open={showDNRWarning} onOpenChange={setShowDNRWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={24} />
              Do Not Rent Warning
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="font-medium text-red-900 mb-2">
                ⚠️ This guest is on the Do Not Rent list
              </p>
              <p className="text-sm text-red-700">
                <strong>Reason:</strong> {dnrDetails?.reason}
              </p>
            </div>
            <p className="text-sm text-slate-600">
              Contact your manager for override approval.
            </p>
            <Button variant="outline" onClick={() => setShowDNRWarning(false)} className="w-full">
              Cancel Reservation
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Room Status Warning */}
      <Dialog open={showRoomWarning} onOpenChange={setShowRoomWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle size={24} />
              Room is not ready
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-900">
                This room is currently marked as <strong>{roomWarningStatus}</strong>.
              </p>
              <p className="text-sm text-orange-800 mt-2">
                Do you still want to check the guest in?
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowRoomWarning(false)} className="flex-1">
                Change Room
              </Button>
              <Button 
                onClick={() => {
                  if (pendingCheckIn) {
                    createReservationOrCheckIn(true);
                  }
                  setShowRoomWarning(false);
                }} 
                className="flex-1 bg-orange-600"
              >
                Override & Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ID Scanner Modal */}
      <IDScannerModal
        open={showScannerModal}
        onClose={() => setShowScannerModal(false)}
        onDataScanned={handleScannedData}
      />

      {/* Quick Add Room Modal */}
      <QuickAddRoomModal
        open={showQuickAddRoomModal}
        onClose={() => setShowQuickAddRoomModal(false)}
        propertyId={selectedProperty?.id}
        onSuccess={() => {
          fetchCalendarData();
          setShowQuickAddRoomModal(false);
        }}
      />

      {/* OOO Maintenance Modal */}
      <OOOModal
        open={showOOOModal}
        onClose={() => {
          setShowOOOModal(false);
          setSelectedRoomForOOO(null);
        }}
        room={selectedRoomForOOO}
        propertyId={selectedProperty?.id}
        onSuccess={() => {
          // Optimistic update — reflect OOO immediately in the UI
          if (selectedRoomForOOO) {
            setCalendarData(prev => {
              if (!prev?.units) return prev;
              return {
                ...prev,
                units: prev.units.map(u => u.id === selectedRoomForOOO.id ? { ...u, status: 'OOO', maintenance_status: 'OOO' } : u)
              };
            });
          }
          setShowOOOModal(false);
          setSelectedRoomForOOO(null);
          fetchCalendarData();
        }}
      />

      {/* Folio Modal */}
      <FolioModal
        open={showFolioModal}
        onClose={() => { setShowFolioModal(false); setFolioReservationId(null); }}
        reservationId={folioReservationId}
      />

      <ReservationEditModal
        open={showEditModal}
        onClose={() => { setShowEditModal(false); setEditReservationId(null); }}
        reservationId={editReservationId}
        propertyId={selectedProperty?.id}
        onSaved={() => { setCalendarVersion(0); fetchCalendarData(); }}
      />

      <ChangeRoomModal
        open={showChangeRoomModal}
        onClose={() => { setShowChangeRoomModal(false); setChangeRoomReservation(null); }}
        reservation={changeRoomReservation}
        propertyId={selectedProperty?.id}
        onChanged={() => { setCalendarVersion(0); fetchCalendarData(); }}
      />

      <AddChargeModal
        open={showAddChargeModal}
        onClose={() => { setShowAddChargeModal(false); setAddChargeReservation(null); }}
        reservation={addChargeReservation}
        onAdded={() => { setCalendarVersion(0); fetchCalendarData(); }}
      />
    </div>
  );
};

export default Calendar;
